import { logEvent, setUserId } from "firebase/analytics";
import { analytics } from "./firebaseConfig";
import { userInfoRepository } from "@/services/userInfoRepository";
import { CONFIG } from "@/config";


export enum UserEvent {
  OPEN_SCREEN = "horoscope_open_screen",
  SIGN_IN = "horoscope_sign_in",
  SIGN_OUT = "horoscope_sign_out",
  SIGN_UP = "horoscope_sign_up",
  RESET_PASSWORD = "horoscope_reset_password",
  SELECT_CARD_OF_THE_DAY = "horoscope_select_card_of_the_day",
  SELECT_LUCKY_CARD = "horoscope_select_lucky_card",
  DELETE_ACCOUNT = "horoscope_delete_account",
  SELECT_SUBSCRIPTION = "horoscope_select_subscription",
  CANCEL_RECURRENT_SUBSCRIPTION = "horoscope_cancel_recurrent_subscription",
}

export interface MetricsInfo {
    user_age: string;
    has_premium: string|null;
    base_url: string;
    app_user_id: string|null;
}

// Standard GA4 event parameters
export interface StandardEventParams {
    screen_name?: string;
    screen_class?: string;
    method?: string;
    currency?: string;
    value?: string;
}

// Custom event parameters for horoscope app
export interface CustomEventParams {
    is_recurrent?: string;
    duration?: string;
}

export interface EventParams extends MetricsInfo, StandardEventParams, CustomEventParams {
    [key: string]: string | null | undefined;
}

function getAge(birthday: Date): string {
  const today = new Date();
  const birthDate = new Date(birthday);

  if (birthDate > today) {
    return "0";
  }
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age.toString();
}

const getUserInfo = async (has_premium: boolean|null): Promise<{ userInfo: MetricsInfo | null; userId: string | null }> => {
  try {
    const userInfo = await userInfoRepository.getUserInfo();
    if (userInfo) {
      return {
        userInfo: {
          user_age: getAge(userInfo.birthday ?? new Date()),
          has_premium: has_premium==null ? null : has_premium.toString(),
          base_url: CONFIG.currentUrl || "",
          app_user_id: userInfo.id?.toString() || null,
        },
        userId: userInfo.id?.toString() || null
      };
    }
    return { userInfo: null, userId: null };
  } catch (error) {
    console.error("Error getting user info for analytics:", error);
    return { userInfo: null, userId: null };
  }
}

const logAnalyticsEvent = async (eventName: string, eventParams: EventParams) => {
  try {
    if (!analytics) {
      console.warn("Firebase Analytics not initialized");
      return;
    }
    await logEvent(analytics, eventName, eventParams);
  } catch (error) {
    console.error(`Error logging event ${eventName}:`, error);
  }
}

const logEventWithUserInfo = async (eventName: string, has_premium: boolean|null, additionalParams: Partial<EventParams> = {}) => {
  try {
    const { userInfo, userId } = await getUserInfo(has_premium);
    
    // Set User-ID for cross-device tracking
    if (userId && analytics) {
      setUserId(analytics, userId);
    }
    
    if (userInfo) {
      await logAnalyticsEvent(eventName, {
        ...userInfo,
        app_user_id: userInfo.app_user_id || null,
        ...additionalParams
      });
    }
  } catch (error) {
    console.error(`Error in ${eventName}:`, error);
  }
}

export const screenLog = async (screenName: string, has_premium: boolean) => {
  await logEventWithUserInfo(UserEvent.OPEN_SCREEN, has_premium, {
    screen_name: screenName
  });
};

export const signInLog = async (has_premium: boolean|null=null) => {
  await logEventWithUserInfo(UserEvent.SIGN_IN, has_premium);
};
  
export const signOutLog = async (has_premium: boolean|null=null) => {
  try {
    await logEventWithUserInfo(UserEvent.SIGN_OUT, has_premium);
    
    // Clear User-ID when user signs out
    if (analytics) {
      setUserId(analytics, null);
    }
  } catch (error) {
    console.error("Error in signOutLog:", error);
  }
};

export const deleteAccountLog = async (has_premium: boolean|null=null) => {
  try {
    await logEventWithUserInfo(UserEvent.DELETE_ACCOUNT, has_premium);
    
    // Clear User-ID when account is deleted
    if (analytics) {
      setUserId(analytics, null);
    }
  } catch (error) {
    console.error("Error in deleteAccountLog:", error);
  }
};

export const signUpLog = async (has_premium: boolean|null=null) => {
  await logEventWithUserInfo(UserEvent.SIGN_UP, has_premium);
};

export const resetPasswordLog = async (has_premium: boolean|null=null) => {
  await logEventWithUserInfo(UserEvent.RESET_PASSWORD, has_premium);
};

export const selectCardOfTheDayLog = async (has_premium: boolean|null=null) => {
  await logEventWithUserInfo(UserEvent.SELECT_CARD_OF_THE_DAY, has_premium);
};

export const selectLuckyCardLog = async (has_premium: boolean|null=null) => {
  await logEventWithUserInfo(UserEvent.SELECT_LUCKY_CARD, has_premium);
};

export const selectSubscriptionLog = async (isRecurrent: boolean, duration: number) => {
  await logEventWithUserInfo(UserEvent.SELECT_SUBSCRIPTION, false, {
    is_recurrent: isRecurrent.toString(),
    duration: duration.toString(),
  });
};

export const cancelRecurrentSubscriptionLog = async () => {
  await logEventWithUserInfo(UserEvent.CANCEL_RECURRENT_SUBSCRIPTION, false);
};
